import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const STORAGE_KEY = 'allure_edge_hint_shown_v1';
// Routes that are considered "root" — no back-history, so showing the hint
// here would be misleading.
const ROOT_PATTERNS = [
  /^\/login$/,
  /^\/register$/,
  /^\/verify-email/,
  /^\/role-select$/,
  /^\/$/,
];

const isRootPath = (pathname) => ROOT_PATTERNS.some((re) => re.test(pathname));

/**
 * One-shot subtle "shimmer" on the left edge of the screen the first time a
 * user lands on a non-root page in the app. Teaches the swipe-back gesture
 * without an explicit tutorial. Fires once per device (gated by localStorage).
 */
export const EdgeSwipeHint = ({ pathname }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (localStorage.getItem(STORAGE_KEY)) return undefined;
    if (isRootPath(pathname)) return undefined;

    const startT = setTimeout(() => {
      setShow(true);
      localStorage.setItem(STORAGE_KEY, '1');
    }, 600);
    const stopT = setTimeout(() => setShow(false), 600 + 1500);

    return () => {
      clearTimeout(startT);
      clearTimeout(stopT);
    };
  }, [pathname]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="edge-swipe-hint"
          aria-hidden="true"
          initial={{ opacity: 0, x: -6 }}
          animate={{ opacity: [0, 0.85, 0.85, 0], x: [-6, 14, 18, 24] }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], times: [0, 0.25, 0.6, 1] }}
          style={{
            position: 'fixed',
            top: '38%',
            left: 0,
            width: 4,
            height: '24%',
            borderRadius: '0 999px 999px 0',
            background: 'linear-gradient(to right, rgba(242, 58, 17, 0.95), rgba(242, 58, 17, 0))',
            boxShadow: '0 0 18px rgba(242, 58, 17, 0.55)',
            pointerEvents: 'none',
            zIndex: 60,
          }}
        />
      )}
    </AnimatePresence>
  );
};

export default EdgeSwipeHint;
